import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Music, Search, Upload, Play, Pause, X, Loader2, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Shared preview player: only one preview plays at a time across the page. */
let currentAudio = null;
export function usePreviewPlayer() {
  const [playingUrl, setPlayingUrl] = useState(null);
  const ref = useRef(null);
  const toggle = (url) => {
    if (ref.current && playingUrl === url) { ref.current.pause(); setPlayingUrl(null); return; }
    if (currentAudio) { currentAudio.pause(); }
    const a = new Audio(url);
    a.volume = 0.8;
    a.onended = () => setPlayingUrl(null);
    a.play().catch(() => toast.error("Não foi possível tocar a prévia"));
    ref.current = a; currentAudio = a; setPlayingUrl(url);
  };
  useEffect(() => () => { ref.current?.pause(); }, []);
  return { playingUrl, toggle };
}

/** Auto-plays a post's music (looped) while `active` is true; stops on deactivate/unmount. Returns { playing, toggle }. */
export function useMusicAutoplayControls(music, active) {
  const url = active ? music?.preview_url : null;
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  useEffect(() => {
    if (!url) { setPlaying(false); return; }
    if (currentAudio) currentAudio.pause();
    const a = new Audio(url);
    a.loop = true;
    a.volume = 0.8;
    a.onplay = () => setPlaying(true);
    a.onpause = () => setPlaying(false);
    a.play().catch(() => setPlaying(false));
    currentAudio = a;
    audioRef.current = a;
    return () => {
      a.pause();
      a.src = "";
      if (currentAudio === a) currentAudio = null;
      if (audioRef.current === a) audioRef.current = null;
      setPlaying(false);
    };
  }, [url]);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setPlaying(false)); else a.pause();
  };
  return { playing, toggle };
}

export function useMusicAutoplay(music, active) {
  return useMusicAutoplayControls(music, active).playing;
}

/** Small floating chip shown over fullscreen media while music plays. */
export function MusicNowPlaying({ music, playing, testid = "music-now-playing" }) {
  if (!music?.preview_url) return null;
  return (
    <div data-testid={testid} className="pointer-events-none inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-black/60 backdrop-blur-md text-white text-xs shadow-lg">
      <span className="relative w-6 h-6 rounded-full overflow-hidden bg-white/10 flex items-center justify-center shrink-0">
        {music.cover ? <img src={music.cover} alt="" className={`absolute inset-0 w-full h-full object-cover ${playing ? "animate-spin [animation-duration:4s]" : ""}`} /> : <Music className="w-3 h-3" />}
      </span>
      <span className="truncate max-w-[220px]"><b>{music.title}</b>{music.artist ? ` · ${music.artist}` : ""}</span>
      <Music className={`w-3 h-3 ${playing ? "animate-pulse" : "opacity-60"}`} />
    </div>
  );
}

/** "Música" — search Deezer (30s previews) or upload own audio. value = {source,title,artist,preview_url,cover,deezer_id}. */
export function MusicPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const fileRef = useRef(null);
  const { playingUrl, toggle } = usePreviewPlayer();

  useEffect(() => {
    if (!open || mode !== "search" || q.trim().length < 2) { setResults([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/music/search`, { params: { q, limit: 10 }, signal: ctrl.signal }); setResults(r.data.results || []); }
      catch (e) { if (!axios.isCancel(e)) toast.error("Erro ao buscar no Deezer"); }
      setLoading(false);
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open, mode]);

  const pick = (t) => { onChange(t); setOpen(false); toast.success(`🎵 ${t.title}${t.artist ? " — " + t.artist : ""}`); };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Selecione um arquivo de áudio"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Áudio muito grande (máx. 15MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url || r.data.file_url;
      pick({ source: "upload", title: uploadTitle.trim() || file.name.replace(/\.[^.]+$/, ""), artist: null, preview_url: url, cover: null });
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no upload"); }
    setUploading(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="music-picker-btn">
            <Music className="w-4 h-4" /> Música
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-2" data-testid="music-picker-popover">
          <div className="inline-flex rounded-full bg-muted p-0.5 mb-2 text-xs">
            <button type="button" onClick={() => setMode("search")} className={`px-3 py-1 rounded-full ${mode === "search" ? "bg-background shadow font-semibold" : ""}`} data-testid="music-mode-search">Buscar (Deezer)</button>
            <button type="button" onClick={() => setMode("upload")} className={`px-3 py-1 rounded-full ${mode === "upload" ? "bg-background shadow font-semibold" : ""}`} data-testid="music-mode-upload">Meu áudio</button>
          </div>
          {mode === "search" ? (
            <>
              <div className="relative mb-2">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome da música ou artista..." className="pl-8 h-9" data-testid="music-search-input" autoFocus />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-0.5" data-testid="music-results">
                {loading && <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
                {!loading && q.trim().length >= 2 && results.length === 0 && <div className="text-xs text-muted-foreground text-center py-3">Nada encontrado</div>}
                {results.map((t) => (
                  <div key={t.deezer_id} className="flex items-center gap-2 px-1.5 py-1.5 rounded-md hover:bg-accent" data-testid={`music-result-${t.deezer_id}`}>
                    <button type="button" onClick={() => toggle(t.preview_url)} className="relative w-9 h-9 shrink-0 rounded overflow-hidden bg-muted" data-testid={`music-preview-${t.deezer_id}`} aria-label="Ouvir prévia">
                      {t.cover && <img src={t.cover} alt="" className="w-full h-full object-cover" />}
                      <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">{playingUrl === t.preview_url ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</span>
                    </button>
                    <button type="button" onClick={() => pick(t)} className="flex-1 min-w-0 text-left" data-testid={`music-select-${t.deezer_id}`}>
                      <div className="text-sm font-medium truncate">{t.title}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{t.artist} · prévia 30s</div>
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-2 text-right">Prévias de 30s via Deezer</div>
            </>
          ) : (
            <div className="space-y-2">
              <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="Nome da faixa (opcional)" className="h-9" data-testid="music-upload-title" />
              <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="music-upload-input" />
              <Button type="button" variant="outline" className="w-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="music-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Escolher áudio do celular
              </Button>
              <div className="text-[10px] text-muted-foreground">MP3, WAV, OGG, M4A ou AAC · até 15MB</div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {value && (
        <span className="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium max-w-[260px]" data-testid="music-chip">
          {value.cover ? <img src={value.cover} alt="" className="w-4 h-4 rounded-sm" /> : <Music className="w-3 h-3" />}
          <span className="truncate">{value.title}{value.artist ? ` — ${value.artist}` : ""}</span>
          <button type="button" onClick={() => onChange(null)} className="hover:text-destructive" aria-label="Remover música" data-testid="music-chip-remove"><X className="w-3 h-3" /></button>
        </span>
      )}
    </div>
  );
}

/** "Local" — free-text place name shown as a clickable tag on the post. value = {name}. */
export function LocationPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(value?.name || "");
  const save = () => { const n = name.trim(); onChange(n ? { name: n.slice(0, 120) } : null); setOpen(false); };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="location-picker-btn">
            <MapPin className="w-4 h-4" /> Local
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2" data-testid="location-picker-popover">
          <div className="text-xs font-semibold mb-1">Onde você está?</div>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Ex.: Praia de Copacabana, Rio" className="h-9" data-testid="location-input" autoFocus />
            <Button type="button" size="sm" onClick={save} data-testid="location-save">OK</Button>
          </div>
        </PopoverContent>
      </Popover>
      {value?.name && (
        <span className="inline-flex items-center gap-1 pl-2 pr-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-medium max-w-[240px]" data-testid="location-chip">
          <MapPin className="w-3 h-3" /><span className="truncate">{value.name}</span>
          <button type="button" onClick={() => { onChange(null); setName(""); }} className="hover:text-destructive" aria-label="Remover local" data-testid="location-chip-remove"><X className="w-3 h-3" /></button>
        </span>
      )}
    </div>
  );
}

/** Published post/story tags: music (with 30s player) + clickable location. */
export function MusicTag({ music, testid }) {
  const { playingUrl, toggle } = usePreviewPlayer();
  if (!music?.preview_url) return null;
  const playing = playingUrl === music.preview_url;
  return (
    <button type="button" onClick={() => toggle(music.preview_url)} data-testid={testid}
      className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-xs transition-colors ${playing ? "bg-primary text-primary-foreground border-primary" : "bg-muted/60 hover:bg-muted border-border"}`}>
      <span className="relative w-6 h-6 rounded-full overflow-hidden bg-black/80 flex items-center justify-center shrink-0">
        {music.cover && <img src={music.cover} alt="" className={`absolute inset-0 w-full h-full object-cover ${playing ? "animate-spin [animation-duration:4s]" : ""}`} />}
        <span className="relative text-white">{playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}</span>
      </span>
      <span className="truncate max-w-[220px]"><b>{music.title}</b>{music.artist ? ` · ${music.artist}` : music.source === "upload" ? " · áudio próprio" : ""}</span>
      <Music className="w-3 h-3 opacity-70" />
    </button>
  );
}

export function LocationTag({ location, testid }) {
  if (!location?.name) return null;
  const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.name)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-border bg-muted/60 hover:bg-emerald-500/10 hover:text-emerald-700 text-xs transition-colors">
      <MapPin className="w-3 h-3" /> {location.name}
    </a>
  );
}
